import React, { useCallback, useEffect, useState } from "react";

import { PATHS } from "../../cloud/config";
import { useCloud } from "../../cloud/context";
import { listDirectory, openFile } from "../../cloud/storage";

import "./OpenFromCloudDialog.scss";

import type { FileNode } from "../../cloud/types";

interface Props {
  excalidrawAPI: any;
  onClose: () => void;
  onOpened: (
    key: string,
    filename: string,
    etag: string | null,
  ) => Promise<void> | void;
}

export const OpenFromCloudDialog: React.FC<Props> = ({
  excalidrawAPI,
  onClose,
  onOpened,
}) => {
  const { user } = useCloud();
  const personalRoot = PATHS.personal(user!.identityId);
  const sharedRoot = PATHS.shared();

  const [currentPrefix, setCurrentPrefix] = useState(personalRoot);
  const [nodes, setNodes] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDir = useCallback(async (prefix: string) => {
    setLoading(true);
    setError(null);
    try {
      const items = await listDirectory(prefix);
      setNodes(items);
    } catch {
      setError("Failed to load directory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDir(currentPrefix);
  }, [currentPrefix, loadDir]);

  const navigateTo = (prefix: string) => {
    setCurrentPrefix(prefix);
  };

  const handleOpen = async (key: string) => {
    setOpening(true);
    setError(null);
    try {
      const data = await openFile(key);
      const filename = key.split("/").pop() ?? key;
      // Bind the autosave hook BEFORE installing the scene — updateScene
      // synchronously fires onChange, which triggers autosave. If we update
      // first, the autosave still sees the previous binding and could
      // overwrite the prior file.
      await onOpened(key, filename, data.etag);
      excalidrawAPI.updateScene({
        elements: data.elements,
        appState: {
          ...data.appState,
          viewBackgroundColor: data.appState.viewBackgroundColor ?? "#ffffff",
          // Clear any local-file handle so a prior local open doesn't linger.
          fileHandle: null,
        },
        files: data.files,
      });
    } catch {
      setError("Failed to open file. Please try again.");
      setOpening(false);
    }
  };

  // Breadcrumb
  const isPersonal = currentPrefix.startsWith(personalRoot);
  const root = isPersonal ? personalRoot : sharedRoot;
  const rootLabel = isPersonal ? "My Files" : "Shared";
  const rest = currentPrefix.replace(root, "").replace(/\/$/, "");
  const parts = rest ? rest.split("/") : [];

  return (
    <div className="open-dialog-overlay" onClick={onClose}>
      <div
        className="open-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Open from cloud"
      >
        <div className="open-dialog-header">
          <h2>Open from Cloud</h2>
          <button
            className="open-dialog-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Section tabs */}
        <div className="open-dialog-tabs">
          <button
            className={`open-dialog-tab${isPersonal ? " active" : ""}`}
            onClick={() => navigateTo(personalRoot)}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm5 5a5 5 0 0 0-10 0h10z" />
            </svg>
            My Files
          </button>
          <button
            className={`open-dialog-tab${!isPersonal ? " active" : ""}`}
            onClick={() => navigateTo(sharedRoot)}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
              <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-3.5 1a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm-3 1a5 5 0 0 0-2 4h1a4 4 0 0 1 2-3.444V8z" />
            </svg>
            Shared
          </button>
        </div>

        {/* Sub-path breadcrumb */}
        {parts.length > 0 && (
          <div className="open-dialog-breadcrumb">
            <button
              className="open-dialog-crumb"
              onClick={() => navigateTo(root)}
            >
              {rootLabel}
            </button>
            {parts.map((part, i) => {
              const prefix = `${root}${parts.slice(0, i + 1).join("/")}/`;
              return (
                <React.Fragment key={prefix}>
                  <span className="open-dialog-sep">›</span>
                  <button
                    className={`open-dialog-crumb${
                      i === parts.length - 1 ? " active" : ""
                    }`}
                    onClick={() => navigateTo(prefix)}
                  >
                    {part}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* File listing */}
        <div className="open-dialog-body">
          {error && (
            <div className="open-dialog-error" onClick={() => setError(null)}>
              {error}
            </div>
          )}

          {loading || opening ? (
            <div className="open-dialog-loading">
              {opening ? "Opening…" : "Loading…"}
            </div>
          ) : (
            <div className="open-dialog-files">
              {/* Up */}
              {currentPrefix !== personalRoot && currentPrefix !== sharedRoot && (
                <button
                  className="open-dialog-item open-dialog-up"
                  onClick={() => {
                    const p = currentPrefix.replace(/\/$/, "").split("/");
                    p.pop();
                    navigateTo(`${p.join("/")}/`);
                  }}
                >
                  <svg
                    viewBox="0 0 16 16"
                    width="14"
                    height="14"
                    fill="currentColor"
                    style={{ opacity: 0.5 }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                    />
                  </svg>
                  Up
                </button>
              )}

              {nodes
                .filter((n) => n.type === "folder")
                .map((node) => (
                  <button
                    key={node.key}
                    className="open-dialog-item open-dialog-folder"
                    onClick={() => navigateTo(node.key)}
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="15"
                      height="15"
                      fill="#f5a623"
                    >
                      <path d="M.54 3.87.5 3a2 2 0 0 1 2-2h3.19a2 2 0 0 1 1.45.63l.41.42H14a2 2 0 0 1 2 2v6.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3.87z" />
                    </svg>
                    {node.name}
                  </button>
                ))}

              {nodes
                .filter((n) => n.type === "file")
                .map((node) => (
                  <button
                    key={node.key}
                    className="open-dialog-item open-dialog-file"
                    onClick={() => handleOpen(node.key)}
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="currentColor"
                      style={{ opacity: 0.45 }}
                    >
                      <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0H4zm0 1h5.5v3A1.5 1.5 0 0 0 11 5.5h3V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                    </svg>
                    {node.name.replace(".excalidraw", "")}
                  </button>
                ))}

              {nodes.length === 0 && (
                <span className="open-dialog-empty">No files here</span>
              )}
            </div>
          )}
        </div>

        <div className="open-dialog-footer">
          <button className="open-dialog-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
